import { Fragment } from 'react'
import { useAuth } from 'react-oidc-context'
import { Button, DialogContent, Divider, Grid, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import { useHost } from '../../providers/app-provider'
import { DeviceTableRow } from '../../routes/accounts/components/devices-tab-panel/types'
import Api from '../../utils/api'
import DialogWithTitle from '../dialog-with-title'
import FormikDropdown from '../formik-dropdown'
import FormikTextField from '../formik-textfield'

import useStyles from './index.styles'

interface DeviceDeactivateModalProps {
  deactivateDevices: DeviceTableRow[] | IDeviceAPIObject[]
  open: boolean
  onClose: () => void
  onContinue: () => void
}

interface IFormValues {
  orderNumber: string
  reason: string
  notes: string
  deviceIds: number[]
}

const deactivateReasons = [
  { id: 'CustomerDamagedDevice', name: 'Customer Damaged Device' },
  { id: 'CustomerInvoluntaryOptOut', name: 'Customer Involuntary OptOut' },
  { id: 'CustomerVoluntaryOptOut', name: 'Customer Voluntary OptOut' },
  { id: 'EquipmentUpgrade', name: 'Equipment Upgrade' },
  { id: 'ExtraUnit', name: 'Extra Unit' },
  { id: 'FaultyEquipNonWarranty', name: 'Faulty Equipment - Non-Warranty' },
  { id: 'FaultyEquipWarranty', name: 'Faulty Equipment - Warranty' },
  { id: 'FaultyEquipWarrantyOverride', name: 'Faulty Equipment - Warranty Override' },
  { id: 'IncorrectItem', name: 'Incorrect Item' },
  { id: 'ShippingDamage', name: 'Shipping Damage' },
  { id: 'CustomerLostDevice', name: 'Customer Lost Device' },
].sort((a, b) => a.name.localeCompare(b.name))

const schema = Yup.object().shape({
  reason: Yup.string().required('Please select a reason before continuing.'),
  notes: Yup.string().when('reason', (reason) => {
    if (reason?.length && reason[0] === 'Other') {
      return Yup.string().required('Notes are required').max(255, 'Must be less than 255 characters')
    }
    return Yup.string().max(255, 'Must be less than 255 characters')
  }),
})

const DeviceDeactivateModal = ({ deactivateDevices, open, onClose, onContinue }: DeviceDeactivateModalProps) => {
  const classes = useStyles
  const { enqueueSnackbar } = useSnackbar()
  const auth = useAuth()
  const host = useHost()

  const initialValues: IFormValues = {
    orderNumber: '',
    reason: '',
    notes: '',
    deviceIds: deactivateDevices.map((d) => d.id),
  }

  const onSubmit = async (values: IFormValues) => {
    if (!host || !auth.user?.access_token) return
    const payload = {
      deviceIds: values.deviceIds,
      notes: values.notes,
      reason: values.reason,
      rmaNumber: values.orderNumber,
    }

    const res = await Api.post(`${host}/api/v2/central/devices/deactivate`, payload)
    if (res.ok) {
      onClose()
      onContinue()
      enqueueSnackbar(`All selected devices have been deactivated.`)
    } else {
      enqueueSnackbar('Could not deactivate', { variant: 'error' })
    }
  }

  return (
    <DialogWithTitle open={open} title="Deactivate" setOpen={onClose}>
      <DialogContent>
        <Formik<IFormValues> initialValues={initialValues} onSubmit={onSubmit} validationSchema={schema}>
          {({ dirty, isValid, isSubmitting }) => (
            <Form>
              <Grid container={true}>
                <Grid size={6}>
                  <FormikTextField name="orderNumber" label="Order #" fullWidth={true} placeholder="Optional" />
                </Grid>

                <Grid size={12}>
                  <FormikDropdown name="reason" label="Reason" required={false} items={deactivateReasons} size="medium" />
                </Grid>

                <Grid size={12}>
                  <FormikTextField name="notes" label="Additional Notes" fullWidth={true} multiline={true} rows={2} />
                </Grid>

                <Grid
                  container={true}
                  size={12}
                  sx={{
                    ml: 1,
                    mt: 1,
                  }}
                >
                  <Grid size={12}>
                    <Typography variant="subtitle2">Deactivate Item{deactivateDevices.length > 1 ? `s (${deactivateDevices.length})` : ''}</Typography>
                    <Divider />
                  </Grid>

                  <Grid sx={classes.deactivateItemsList} size={12}>
                    {deactivateDevices.map((device) => (
                      <Fragment key={device.id}>
                        <Typography
                          variant="body2"
                          sx={{
                            my: 1,
                          }}
                        >
                          {device.vanity} • {device.uniqueId} • {device.nodeType.vanityName}
                        </Typography>
                        <Divider />
                      </Fragment>
                    ))}
                  </Grid>
                </Grid>

                <Grid
                  container={true}
                  size={12}
                  sx={{
                    justifyContent: 'flex-end',
                    gap: 2,
                    mt: 1,
                  }}
                >
                  <Button variant="outlined" onClick={onClose}>
                    Cancel
                  </Button>

                  <Button type="submit" variant="contained" disabled={!dirty || !isValid || isSubmitting}>
                    Deactivate Device{deactivateDevices.length > 1 ? 's' : ''}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </DialogWithTitle>
  )
}

export default DeviceDeactivateModal
